import cn from 'classnames';
import { useRef, type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { getSrcsetString } from 'dibs-image-utils/exports/srcSet.ts';
import { VideoPlayer } from 'dibs-video/exports/VideoPlayer.ts';
import { buildMuxUrl } from 'dibs-video/exports/helpers.ts';
import { useSelector } from 'react-redux';

// styles
import dibsCss from 'dibs-css';
import styles from './SbSharedContentModuleLandingVideo.module.css';

import { type SbSharedContentModuleLandingVideo_viewer$key } from './__generated__/SbSharedContentModuleLandingVideo_viewer.graphql.ts';

const viewerFragment = graphql`
    fragment SbSharedContentModuleLandingVideo_viewer on Viewer {
        muxMonitoringFF: featureFlag(feature: "muxMonitoring")
    }
`;

type RelayVariables = { userId: string; isClient: boolean };

type ReduxStoreType = {
    relayVariables: {
        variables: RelayVariables;
    };
};

export const SbSharedContentModuleLandingVideo: FC<{
    id: string;
    width: number;
    height: number;
    title?: string;
    thumbnailUrl?: string;
    isMuxVideo?: boolean;
    viewer?: SbSharedContentModuleLandingVideo_viewer$key | null;
}> = ({ id, thumbnailUrl, width, height, title, isMuxVideo, viewer: viewerRef }) => {
    const imageOnly = !id && thumbnailUrl;
    const videoPlayerRef = useRef<HTMLVideoElement>(null);

    const viewer = useFragment(viewerFragment, viewerRef);

    const { isClient, userId } = useSelector<ReduxStoreType, RelayVariables>(
        state => state?.relayVariables?.variables || {}
    );

    const thumbnailPlaceholder = thumbnailUrl ? (
        <img
            alt=""
            sizes="100vw"
            src={thumbnailUrl}
            srcSet={getSrcsetString(thumbnailUrl)}
            style={{ width, height }}
            className={cn(
                dibsCss.top50,
                dibsCss.left50,
                dibsCss.border0,
                dibsCss.absolute,
                styles.thumbnail
            )}
        />
    ) : null;

    if (imageOnly) {
        return (
            <img
                alt=""
                sizes="100vw"
                src={thumbnailUrl}
                srcSet={getSrcsetString(thumbnailUrl)}
                style={{ width, height }}
                className={cn(
                    dibsCss.top50,
                    dibsCss.left50,
                    dibsCss.border0,
                    dibsCss.absolute,
                    styles.thumbnail,
                    styles.imageOnly
                )}
            />
        );
    }

    if (isMuxVideo) {
        if (!isClient) {
            return thumbnailPlaceholder;
        }
        return (
            <VideoPlayer ref={videoPlayerRef} fallback={thumbnailPlaceholder}>
                <VideoPlayer.Media
                    monitor={!!viewer?.muxMonitoringFF}
                    monitorMetadata={{
                        viewerUserId: userId,
                    }}
                    playback={{ autoPlay: true, loop: true, muted: true }}
                    src={buildMuxUrl({ playbackId: id })}
                    objectFit="cover"
                />
            </VideoPlayer>
        );
    }

    return (
        <>
            {thumbnailPlaceholder}
            <iframe
                width={width}
                height={height}
                allow="autoplay"
                className={cn(
                    dibsCss.top50,
                    dibsCss.left50,
                    dibsCss.border0,
                    dibsCss.absolute,
                    styles.iframe
                )}
                title={`${title || ''}_${id.toString()}`}
                referrerPolicy="strict-origin-when-cross-origin"
                src={`https://player.vimeo.com/video/${id}?badge=0&background=1&player_id=0&app_id=143881`}
            />
        </>
    );
};
